import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, ISOTimestamp, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/**
 * ══ HỢP ĐỒNG API — DANH MỤC ĐỐI TÁC (khách hàng · nhà cung cấp · chia hoa hồng) ═══════════════
 *
 * ⚠ HAI BÊN BẤT ĐỐI XỨNG, đừng gộp lược đồ: bản vá NHÀ CUNG CẤP cho `null` ("xoá trắng ô này") ở mọi ô
 *   (`vendors.ts`, `VendorPatch`); bản vá KHÁCH HÀNG chỉ cho `null` ở ba ô của hộp ĐIỀU CHỈNH HẠN MỨC / cờ BÁO SAI
 *   (`creditAmount` · `creditAdjNote` · `creditReviewNote` — rà 21/10 dashboard-2), các ô khác `undefined` = không đụng.
 */

const customerFields = {
  invoiceName: z.string().optional(),
  tax: z.string().optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  docEmail: z.string().optional(),
  repPosition: z.string().optional(),
  /** `null` = XOÁ ô (Danh mục đối tượng AMIS gửi `trim() || null` như bản cũ `|| undefined`, `AmisDocs.tsx:488, :492`). */
  bankAccount: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  acctName: z.string().optional(),
  acctPhone: z.string().optional(),
  acctEmail: z.string().optional(),
  contactName: z.string().optional(),
  contactPerson: z.string().optional(),
  /** % VAT — KHÔNG phải tiền (NUMERIC(5,2)), nên là `number` chứ không phải `MoneyIn`. */
  lastVatRate: blankAsAbsent(z.coerce.number()),
  creditAmount: blankAsAbsent(moneyField('Hạn mức công nợ')),
  creditAdjNote: z.string().optional(),
  creditAdjBy: z.string().optional(),
  creditAdjAt: blankAsAbsent(ISOTimestamp),
  salesNote: z.string().optional(),
  customerNote: z.string().optional(),
  creditReviewNote: z.string().optional(),
  isSupplier: z.boolean().optional(),
  salesMainId: blankAsAbsent(Uuid),
  salesSubId: blankAsAbsent(Uuid),
  salesMainName: z.string().optional(),
  salesSubName: z.string().optional(),
  splitMain: blankAsAbsent(z.coerce.number()),
  splitSub: blankAsAbsent(z.coerce.number()),
  splitStatus: z.enum(['pending', 'approved', 'rejected']).optional(),
  active: z.boolean().optional(),
}

/**
 * ⚠ `creditDays` là TUỲ CHỌN ở đây dù `NewCustomer` khai nó không có `?`.
 *
 * Kiểu TypeScript đang NÓI SAI về hành vi thật: không có xác thực lúc chạy, nên mọi lời gọi thiếu
 * `creditDays` vẫn đi qua và cột nhận DEFAULT của CSDL (30 ngày). Cả bộ test hiện có tạo khách
 * hàng đúng kiểu đó. Bắt buộc nó ở biên là **thêm một ràng buộc MỚI** vào một API đang chạy, không
 * phải "kiểm thứ vốn đã bắt buộc" — và làm vỡ mọi nơi gọi đang hoạt động.
 *
 * Luật "phải nhập số ngày công nợ" là luật của FORM bản cũ (`alertCreditDaysRequired`), thuộc về
 * màn hình. Muốn nâng nó thành luật máy chủ thì mở `DIEM-CAN-QUYET-DINH.md` — đó là đổi hành vi,
 * không phải dọn mã.
 */
export const CreateCustomerBody = z.object({
  code: z.string(),
  name: z.string(),
  creditDays: blankAsAbsent(z.coerce.number().int()),
  ...customerFields,
  /**
   * `true` = hộp TẠO NHANH khách tại ô chọn (form Đơn · Báo giá · chi phí đơn). Bản cũ `QuickCustomerModal`
   * (`Az.tsx:4154-4192`) KHÔNG gán sale chính = người bấm như Danh mục (`withSale`, `Catalog.tsx:3521-3531`) —
   * sale bổ sung sau ở Danh mục ▸ Khách hàng. Không phải cột của bảng; máy chủ bỏ ra trước khi ghi.
   */
  skipDefaultSale: z.boolean().optional(),
  /**
   * K01839 — `true` = hộp ＋ Thêm KH ở AR ▸ Hạn mức. Hộp bản cũ (`AR.tsx:2668-2683`) chỉ có mã · tên · số ngày · hạn mức · ghi
   * chú Sales, KHÔNG bắt MST ⇒ máy chủ bỏ qua `guardTax` cho riêng đường này (Danh mục vẫn bắt MST như form bản cũ).
   * Không phải cột của bảng; máy chủ bỏ ra trước khi ghi.
   */
  allowNoTax: z.boolean().optional(),
  /**
   * `true` = NHẬP HÀNG LOẠT ở Danh mục ▸ Khách hàng. Bản cũ `importMany` (`Catalog.tsx:3538-3551`) đặt người nhập làm sale
   * chính 100% và ghi lịch sử "Nhập hàng loạt — sale chính X · 100%". Không phải cột của bảng.
   */
  viaImport: z.boolean().optional(),
})

export const UpdateCustomerBody = z.object({
  version: Version,
  /**
   * `true` = form SỬA của Danh mục ▸ Khách hàng — máy chủ chạy `withSale` như bản cũ (`Catalog.tsx:3531`
   * `c.id ? withSale(c) : …`): khách chưa có sale chính ⇒ người lưu thành sale chính 100%. Không phải cột của bảng.
   */
  defaultSale: z.boolean().optional(),
  code: z.string().optional(),
  name: z.string().optional(),
  creditDays: blankAsAbsent(z.coerce.number().int()),
  ...customerFields,
  /**
   * `null` = XOÁ TRẮNG hạn mức tiền (L43, soát 16/10 — bản cũ `AR.tsx:2683` `creditAmount: n || undefined`). Trước đây chỉ nhận
   * số: xoá trắng ô thì màn không gửi gì, hạn mức cũ còn nguyên mà dấu "đã điều chỉnh" vẫn đóng. Chuỗi rỗng vẫn là "không đụng".
   */
  creditAmount: blankAsAbsent(moneyField('Hạn mức công nợ').nullable()),
  /**
   * `null` = XOÁ ghi chú điều chỉnh / GỠ cờ "Sale báo sai" (rà 21/10 dashboard-2). Bản cũ ghi `undefined` = xoá trường:
   *   hộp Điều chỉnh hạn mức `creditReviewNote: undefined` — "kế toán đã điều chỉnh → gỡ cờ báo sai", `creditAdjNote:
   *   note.trim() || undefined` (`Catalog.tsx:3106-3113`); hộp HĐ nhanh "Để trống = gỡ cờ báo sai" (`AzContract.tsx:720-722`).
   *   Trước đây lược đồ từ chối `null` ⇒ hộp HĐ nhanh gỡ cờ ra 400, còn hộp hạn mức gửi `undefined` nên dòng đỏ không tắt.
   */
  creditAdjNote: z.string().nullable().optional(),
  creditReviewNote: z.string().nullable().optional(),
  /** `null` = XOÁ ghi chú Sales (K01838 — hộp hạn mức bản cũ `AR.tsx:2679` ghi chuỗi rỗng khi xoá trắng ô). */
  salesNote: z.string().nullable().optional(),
})

const vendorFields = {
  code: z.string().nullable().optional(),
  tax: z.string().nullable().optional(),
  paymentTermDays: z.number().int().nullable().optional(),
  phone: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
  bankAccount: z.string().nullable().optional(),
  bankName: z.string().nullable().optional(),
  bankHolder: z.string().nullable().optional(),
  isCustomer: z.boolean().nullable().optional(),
  address: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  docEmail: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  contactPerson: z.string().nullable().optional(),
  repPosition: z.string().nullable().optional(),
  acctName: z.string().nullable().optional(),
  acctPhone: z.string().nullable().optional(),
  acctEmail: z.string().nullable().optional(),
  portalEnabled: z.boolean().nullable().optional(),
  portalEmail: z.string().nullable().optional(),
  isFuelVendor: z.boolean().nullable().optional(),
  active: z.boolean().nullable().optional(),
}

export const CreateVendorBody = z.object({ name: z.string(), ...vendorFields })
export const UpdateVendorBody = z.object({
  version: Version,
  name: z.string().nullable().optional(),
  ...vendorFields,
})

/** ⇤ Gộp NCC trùng: giữ `keepId`, trỏ mọi tham chiếu của `dropIds` sang rồi xoá. `dryRun` = chỉ đếm cho hộp xác nhận. */
export const MergeVendorsBody = z.object({
  keepId: Uuid,
  dropIds: z.array(Uuid).min(1, 'Chưa chọn bản nào để gộp vào.').max(50),
  dryRun: z.boolean().optional(),
})
export const DeleteVendorBody = z.object({ version: Version })
export type MergeVendorsBodyT = z.infer<typeof MergeVendorsBody>
export type DeleteVendorBodyT = z.infer<typeof DeleteVendorBody>

export const SubmitSalesSplitBody = z.object({
  version: Version,
  /** % của sale PHỤ. % sale chính tự suy = 100 − splitSub. */
  splitSub: z.number().min(0).max(100),
  effectiveFrom: ISODate,
  salesMainId: blankAsAbsent(Uuid.nullable()),
  salesSubId: blankAsAbsent(Uuid.nullable()),
  salesMainName: z.string().nullable().optional(),
  salesSubName: z.string().nullable().optional(),
  splitNote: z.string().nullable().optional(),
  splitApproverId: blankAsAbsent(Uuid.nullable()),
})

export const DecideSalesSplitBody = z.object({
  version: Version,
  approve: z.boolean({ required_error: 'Thiếu `version` hoặc `approve`.' }),
})

export const PartnerIdParams = z.object({ id: Uuid })

/**
 * ══ BỐN Ô KPI CỦA MÀN DANH MỤC — bấm một ô là LỌC BẢNG xuống đúng nhóm đó ════════════════════
 *
 * Bốn định nghĩa nghiệp vụ, không phải bốn kiểu hiển thị (bản cũ `Catalog.tsx:3465-3478`):
 *   · `noContract` — chưa có hợp đồng nào ở trạng thái `signed`. ⚠ KHÁC "không có hợp đồng nào":
 *     đối tác có ba bản nháp vẫn là CHƯA KÝ.
 *   · `pending`    — có hợp đồng đang chờ duyệt (`proposed`).
 *   · `expiring`   — có hợp đồng ĐÃ KÝ hết hạn trong 30 ngày tới. Hợp đồng HẾT HẠN RỒI thì không
 *     tính — nó thuộc `noContract`, và trộn hai nhóm là làm mất cảnh báo sớm.
 *   · `overdue`    — còn tờ nợ ĐÃ QUÁ HẠN. Mốc tuổi nợ do sổ công nợ quyết, màn không tự so ngày.
 *
 * ⚠ LỌC Ở MÁY CHỦ, không ở màn. Trước đây màn tải cả bảng rồi mới lọc — tức phân trang có mà như
 *   không, và bốn con số này phải đếm trên TOÀN BỘ tập đang lọc chứ không phải trên trang đang xem.
 */
export const PartnerKpiFilter = z.enum(['noContract', 'pending', 'expiring', 'overdue'])
export type PartnerKpiFilterT = z.infer<typeof PartnerKpiFilter>

/** Bộ lọc dùng chung cho CẢ ba cửa của một tab (danh sách · bảng · tóm tắt) — khai MỘT lần. */
const CatalogFilterShape = {
  tax: z.string().optional(),
  /** Ô 🔍 — tên · mã · MST. `trim()` trước khi kiểm độ dài: dán một dấu cách là "không lọc". */
  q: z.string().trim().min(1).optional(),
  kpi: PartnerKpiFilter.optional(),
}

export const ListCustomerQuery = PageQuery.extend(CatalogFilterShape)
export const ListVendorQuery = PageQuery.extend(CatalogFilterShape)
export type ListCustomerQueryT = z.infer<typeof ListCustomerQuery>
export type ListVendorQueryT = z.infer<typeof ListVendorQuery>

/** Cửa TÓM TẮT không lật trang — nó đếm, nên không nhận `limit`/`cursor`. */
export const PartnerBoardSummaryQuery = z.object(CatalogFilterShape)
export type PartnerBoardSummaryQueryT = z.infer<typeof PartnerBoardSummaryQuery>

/**
 * ══ TRA MÃ SỐ THUẾ (nút "🔍 Tra MST" của form Khách hàng / Nhà cung cấp) ══════════════════════
 *
 * ⚠ `found: false` KHÁC một lỗi HTTP. Không tra được là chuyện THƯỜNG (MST mới cấp, nhà cung cấp
 *   dữ liệu bận, mạng công ty chặn) và bản cũ xử đúng như vậy: hiện một dòng vàng rồi để người
 *   dùng gõ tay. Trả 4xx/5xx ở đây sẽ biến một chuyện thường thành một sự cố trên màn hình.
 *
 * ⚠ `tax` TUỲ CHỌN, và đó là hệ quả thẳng của câu trên: cửa này LUÔN trả 200, nên "không gửi MST"
 *   cũng phải ra `found: false` chứ không ra 400 — một cửa vừa hứa luôn 200 vừa có nhánh 400 là
 *   một cửa nói hai lời. Bắt buộc `tax` còn làm hỏng lưới quyền `rt-stubs`, vốn gọi THỬ mọi cửa
 *   GET có `rtPath` mà không kèm tham số và đòi 200 hoặc 403.
 */
export const TaxLookupQuery = z.object({ tax: z.string().optional() })
export type TaxLookupQueryT = z.infer<typeof TaxLookupQuery>
